import React, { useEffect, useState } from 'react';
import {
  useTable,
  useBlockLayout,
  useResizeColumns,
  usePagination,
} from 'react-table';
import { COLUMN_WIDTHS_STATE_FIELD } from './constants';
import { usePrevious } from '../../hooks/usePrevious';
import { updateTableSavedTableState } from '../../helpers/tableHelpers';

const ReactTable = ({
  columns,
  data,
  defaultColumn,
  name,
  OtherElements,
  previousPageHandlerFn,
  nextPageHandlerFn,
  goToPage,
  totalPages,
  currentPage,
}) => {
  const {
    getTableProps,
    allColumns,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state: { columnResizing },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: { pageIndex: 0, pageSize: 25 },
    },
    useBlockLayout,
    useResizeColumns,
    usePagination,
  );

  const [isResizingColumn, setIsResizingColumn] = useState(false);
  const isPrevResizingColumn = usePrevious(isResizingColumn);

  useEffect(() => {
    if (columnResizing.isResizingColumn) {
      setIsResizingColumn(true);
    } else {
      setIsResizingColumn(false);
    }
  }, [columnResizing.isResizingColumn]);

  useEffect(() => {
    const hasResized = !isResizingColumn && isPrevResizingColumn;
    if (name && hasResized && columnResizing) {
      const updatedTableState = allColumns.map(({ width, id }) => ({
        width,
        id,
      }));

      updateTableSavedTableState(name, COLUMN_WIDTHS_STATE_FIELD, [
        ...updatedTableState,
      ]);
    }
  }, [
    columnResizing,
    isResizingColumn,
    isPrevResizingColumn,
    name,
    allColumns,
  ]);

  return (
    <div>
      <div className="flex justify-between items-center">
        <div>{OtherElements && <OtherElements />}</div>
        <div className="flex items-center pagination-container">
          <div className="pagination mr-4">
            <button
              type="button"
              className="greenButton"
              onClick={() => goToPage(1)}
            >
              {'<<'}
            </button>{' '}
            <button
              type="button"
              className="greenButton"
              onClick={() => previousPageHandlerFn()}
            >
              {'<'}
            </button>{' '}
            <button
              type="button"
              className="greenButton"
              onClick={() => nextPageHandlerFn()}
            >
              {'>'}
            </button>{' '}
            <button
              type="button"
              className="greenButton"
              onClick={() => goToPage(totalPages)}
            >
              {'>>'}
            </button>{' '}
            <span>
              Page{' '}
              <strong>
                {currentPage} of {totalPages}
              </strong>{' '}
            </span>
          </div>
        </div>
      </div>
      <div {...getTableProps()} className="table">
        <div className="table-header">
          {headerGroups.map((headerGroup) => (
            <div {...headerGroup.getHeaderGroupProps()} className="tr">
              {headerGroup.headers.map((column) => (
                <div {...column.getHeaderProps()} className="th">
                  {column.render('Header')}
                  <div
                    {...column.getResizerProps()}
                    className={`resizer ${
                      column.isResizing ? 'isResizing' : ''
                    }`}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>

        <div {...getTableBodyProps()} className="table-content">
          {page.map((row) => {
            prepareRow(row);
            return (
              <div {...row.getRowProps()} className="tr">
                {row.cells.map((cell) => {
                  return (
                    <div {...cell.getCellProps()} className="td">
                      {cell.render('Cell')}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const Table = ({
  memoizedData,
  memoizedColumns,
  memoizedDefaultColumn,
  name,
  OtherElements,
  previousPageHandlerFn,
  nextPageHandlerFn,
  goToPage,
  totalPages,
  currentPage,
}) => {
  return (
    <ReactTable
      columns={memoizedColumns}
      data={memoizedData}
      defaultColumn={memoizedDefaultColumn}
      name={name}
      OtherElements={OtherElements}
      previousPageHandlerFn={previousPageHandlerFn}
      nextPageHandlerFn={nextPageHandlerFn}
      goToPage={(page) => goToPage(page)}
      totalPages={totalPages}
      currentPage={currentPage}
    />
  );
};
